import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { BookOpen, Edit2, RefreshCcw, Save, X, AlertCircle, Loader2 } from 'lucide-react'

import {
  dramaApi,
  dramaQueryKeys,
  pickLocalized,
  pickLocalizedList,
  hasLocalized,
} from '@/lib/api/drama'
import type { DramaLang, DramaStoryOutline } from '@/types/drama'
import { dramaT } from '@/components/drama/i18n'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { toast } from '@/components/ui/toaster'

interface Props {
  projectId: string
  outline: DramaStoryOutline | null
  lang: DramaLang
}

export function StoryOutlinePanel({ projectId, outline, lang }: Props) {
  const t = dramaT(lang)
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)

  const outlineJson = outline?.outline_json
  const episodes = outlineJson?.episodes ?? []

  // Whether the outline carries any Chinese (drives the "regenerate for
  // bilingual" hint banner below). The language switch itself is now global.
  const bilingual =
    hasLocalized(outlineJson?.overview, 'zh') ||
    episodes.some(
      (ep) =>
        hasLocalized(ep.reader_summary, 'zh') ||
        hasLocalized(ep.title, 'zh') ||
        hasLocalized(ep.ending_hook, 'zh'),
    )

  // The overview text for the currently-selected language (falls back to the
  // other language, then to the flattened `overview` column).
  const overviewText = pickLocalized(outlineJson?.overview, lang) || outline?.overview || ''

  const [draftOverview, setDraftOverview] = useState('')

  const saveMutation = useMutation({
    mutationFn: () => {
      // Write the edited text back into the bilingual structure for the active
      // language, preserving the other language. Also keep the flat `overview`
      // column in sync (prefer English) for non-UI consumers (e.g. render).
      const existing = outlineJson?.overview
      const existingObj =
        existing && typeof existing === 'object' ? existing : {}
      const nextOverview = { ...existingObj, [lang]: draftOverview }
      const nextJson = { ...(outlineJson ?? {}), overview: nextOverview }
      return dramaApi.patchOutline(projectId, {
        overview: nextOverview.en ?? draftOverview,
        outline_json: nextJson,
        is_user_edited: true,
      })
    },
    onSuccess: () => {
      toast({ title: t('toast.outlineSaved'), description: t('toast.outlineSavedDesc') })
      queryClient.invalidateQueries({ queryKey: dramaQueryKeys.detail(projectId) })
      setEditing(false)
    },
    onError: (e: any) => {
      toast({
        title: t('toast.saveFailed'),
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      })
    },
  })

  const regenerateMutation = useMutation({
    mutationFn: () => dramaApi.regenerateOutline(projectId),
    onSuccess: () => {
      toast({ title: t('toast.regenerating'), description: t('toast.regeneratingDesc') })
      queryClient.invalidateQueries({ queryKey: dramaQueryKeys.detail(projectId) })
    },
    onError: (e: any) => {
      toast({
        title: t('toast.regenFailed'),
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      })
    },
  })

  if (!outline) {
    return (
      <div className="text-center py-16 border-2 border-dashed rounded-lg">
        <BookOpen className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">
          {t('story.empty')}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="p-5 border rounded-lg bg-card">
        <div className="flex items-start justify-between mb-3 gap-3">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-spectrum-magenta" />
            <h3 className="text-base font-semibold">{t('story.title')}</h3>
          </div>
          <div className="flex items-center gap-2">
            {!editing && (
              <Button variant="outline" size="sm" onClick={() => { setDraftOverview(overviewText); setEditing(true) }}>
                <Edit2 className="w-3.5 h-3.5 mr-1" />
                {t('story.edit')}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => regenerateMutation.mutate()}
              disabled={regenerateMutation.isPending}
            >
              {regenerateMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              ) : (
                <RefreshCcw className="w-3.5 h-3.5 mr-1" />
              )}
              {t('story.regenerate')}
            </Button>
          </div>
        </div>

        {!bilingual && (
          <p className="text-xs text-muted-foreground mb-3">
            {t('story.singleLangHint')}
          </p>
        )}

        {editing ? (
          <>
            <Textarea
              value={draftOverview}
              onChange={(e) => setDraftOverview(e.target.value)}
              rows={8}
              className="mb-3"
              placeholder={t('story.overviewPlaceholder')}
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditing(false)}
              >
                <X className="w-3.5 h-3.5 mr-1" /> {t('story.cancel')}
              </Button>
              <Button
                size="sm"
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
              >
                <Save className="w-3.5 h-3.5 mr-1" />
                {saveMutation.isPending ? t('story.saving') : t('story.save')}
              </Button>
            </div>
          </>
        ) : (
          <p className="text-sm leading-relaxed text-muted-foreground whitespace-pre-line">
            {overviewText || t('story.noOverview')}
          </p>
        )}
      </div>

      {/* Per-episode breakdown */}
      {episodes.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-3">Episode breakdown ({episodes.length})</h3>
          <div className="space-y-3">
            {episodes.map((ep) => {
              const title = pickLocalized(ep.title, lang)
              const summary = pickLocalized(ep.reader_summary, lang)
              const plotPoints = pickLocalizedList(ep.key_plot_points, lang)
              const hook = pickLocalized(ep.ending_hook, lang)
              return (
                <div key={ep.episode_number} className="p-4 border rounded-md bg-card">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="info">Ep {ep.episode_number}</Badge>
                    {title && <span className="text-sm font-medium">{title}</span>}
                  </div>
                  {summary && <p className="text-sm text-muted-foreground mb-2">{summary}</p>}
                  {plotPoints.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {plotPoints.map((kp, i) => (
                        <Badge key={`${kp}-${i}`} variant="outline">
                          {kp}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {hook && (
                    <div className="flex items-start gap-2 text-xs">
                      <AlertCircle className="w-3.5 h-3.5 text-spectrum-orange mt-0.5 flex-shrink-0" />
                      <span className="text-spectrum-orange">{hook}</span>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
